import {
  EventBus,
  HMSAudioPlugin,
  HMSAudioPluginType,
  HMSPluginSupportResult,
} from "@100mslive/hms-video-store";
import KrispSDK from "./krisp";
import { getAnalyticsEvent } from "./utils";

type AudioFilterNode = Awaited<ReturnType<KrispSDK["createNoiseFilter"]>>;

const FILTER_READY_TIMEOUT_MS = 15_000;
// Matches Krisp's DEFAULT_NOISE_SUPPRESSION_LEVEL (the SDK bundle constant).
const DEFAULT_NOISE_SUPPRESSION_LEVEL = 100;

export class HMSKrispPlugin implements HMSAudioPlugin {
  private sdk: KrispSDK | null = null;
  private readonly TAG = "[HMSKrispPlugin]";
  private filterNode: AudioFilterNode | undefined = undefined;
  private eventBus?: EventBus;
  // Krisp tracks the suppression level on the worklet processor (audio
  // thread). The main-thread AudioWorkletNode exposes setNoiseSuppressionLevel
  // but no read API across the worklet boundary, so we cache the last value
  // set on this plugin instance for consumers (e.g. UI sliders that remount).
  private noiseSuppressionLevel: number = DEFAULT_NOISE_SUPPRESSION_LEVEL;

  checkSupport(): HMSPluginSupportResult {
    return {
      isSupported: KrispSDK.isSupported(),
    };
  }

  setEventBus(eventBus: EventBus) {
    this.eventBus = eventBus;
  }
  async init() {
    this.sdk = new KrispSDK({
      params: {
        debugLogs: false,
        logProcessStats: false,
        useSharedArrayBuffer: false,
        // useBVC: true,
        models: {
          model8: "https://static.100ms.live/krisp/2.3.7/models/krisp-nc-o-nb-v2.kef",
          modelNC: "https://static.100ms.live/krisp/2.3.7/models/krisp-nc-o-med-v7.kef",
        },
      },
    });
    await this.sdk.init();
  }
  getPluginType() {
    return HMSAudioPluginType.TRANSFORM;
  }

  getName() {
    return "HMSKrispPlugin";
  }
  isSupported() {
    return KrispSDK.isSupported();
  }

  toggle() {
    const isKrispEnabled = this.isEnabled();
    const eventName = `mediaPlugin.toggled.${isKrispEnabled ? "off" : "on"}`;
    this.eventBus?.analytics.publish(getAnalyticsEvent(eventName));
    this.filterNode?.toggle();
  }

  isEnabled() {
    return this.filterNode?.isEnabled();
  }

  setNoiseSuppressionLevel(level: number) {
    if (!Number.isFinite(level) || level < 0 || level > 100) {
      throw new Error(`Invalid noise suppression level: ${level}, expected a finite number in [0, 100]`);
    }
    this.noiseSuppressionLevel = level;
    this.filterNode?.setNoiseSuppressionLevel(level);
  }

  getNoiseSuppressionLevel(): number {
    return this.noiseSuppressionLevel;
  }

  async processAudioTrack(ctx: AudioContext, source: AudioNode): Promise<AudioNode> {
    if (!this.sdk) {
      throw new Error(`${this.TAG} processAudioTrack called before init()`);
    }

    // Drop any prior filter; otherwise re-invocation leaks the previous worklet+worker+WASM.
    this.filterNode?.dispose();
    this.filterNode = undefined;

    const filterNode = await new Promise<AudioFilterNode>((resolve, reject) => {
      // Krisp resolves createNoiseFilter with the node synchronously-ish, then fires onReady
      // later when the worker finishes loading the model. We capture the node here so onReady
      // doesn't have to read `this.filterNode` (which would race with the `.then` below).
      let node: AudioFilterNode | undefined;
      const timer = setTimeout(() => {
        reject(
          new Error(
            `${this.TAG} filter node onReady timed out after ${FILTER_READY_TIMEOUT_MS}ms`,
          ),
        );
      }, FILTER_READY_TIMEOUT_MS);

      this.sdk!
        .createNoiseFilter(
          ctx,
          () => {
            if (!node) return;
            clearTimeout(timer);
            resolve(node);
          },
          () => {
            this.filterNode = undefined;
          },
        )
        .then((n) => {
          node = n;
        })
        .catch((e) => {
          clearTimeout(timer);
          reject(e);
        });
    });

    this.filterNode = filterNode;
    filterNode.enable();
    source.connect(filterNode);
    console.log(this.TAG, "filter node created and enabled");
    return filterNode;
  }
  stop() {
    this.filterNode?.dispose();
    this.filterNode = undefined;
    this.sdk = null;
  }
}
